import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Crown, Users, Sparkles, ArrowLeft, Gift, Loader2, AlertCircle, Zap, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [inviteCode, setInviteCode] = useState(() => {
    return searchParams.get('code') || '';
  });
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState('');
  const [planType, setPlanType] = useState('individual'); // 'individual' or 'household'
  const [showInviteCode, setShowInviteCode] = useState(() => {
    // Auto-expand if code is provided via URL
    return !!searchParams.get('code');
  });

  const individualPlans = [
    {
      id: 'individual_monthly',
      name: 'Monthly',
      price: 6.99,
      period: '/mo',
      billing: 'Billed monthly',
      features: [
        'Guapo Vision (AI Statement Scanner) 📸',
        'Plaid Bank Sync',
        'Full AI Financial Insights',
        'Unlimited Bills & Goals',
        'Full History Access',
        '1099 Tax Calculator'
      ],
      icon: Sparkles,
      popular: false
    },
    {
      id: 'individual_quarterly',
      name: 'Quarterly',
      price: 24.99,
      period: '/3 mo',
      billing: 'Billed every 3 months',
      // Removed "Save 20%" - $24.99 > $6.99 × 3 = $20.97
      features: [
        'Everything in Monthly',
        'Priority Support',
        'Advanced Analytics',
        'Export Reports'
      ],
      icon: Zap,
      popular: false
    },
    {
      id: 'individual_annual',
      name: 'Annual',
      price: 59.00,
      period: '/yr',
      billing: 'Billed annually',
      savings: 'Save 30%',
      features: [
        'Everything in Quarterly',
        'Early Access Features',
        'Premium Support',
        'Best Value!'
      ],
      icon: Crown,
      popular: true
    }
  ];

  const householdPlans = [
    {
      id: 'household_monthly',
      name: 'Monthly',
      price: 12.99,
      period: '/mo',
      billing: 'Billed monthly',
      features: [
        'Guapo Vision (AI Statement Scanner) 📸',
        'Everything in Individual',
        'Up to 6 Family Members',
        'Shared Household Dashboard',
        'Generate Invite Codes',
        'Family Financial Goals'
      ],
      icon: Users,
      popular: false
    },
    {
      id: 'household_quarterly',
      name: 'Quarterly',
      price: 34.99,
      period: '/3 mo',
      billing: 'Billed every 3 months',
      savings: 'Save 10%',
      features: [
        'Everything in Monthly',
        'Family Analytics',
        'Shared Budget Templates',
        'Priority Family Support'
      ],
      icon: Users,
      popular: false
    },
    {
      id: 'household_annual',
      name: 'Annual',
      price: 89.00,
      period: '/yr',
      billing: 'Billed annually',
      savings: 'Save 40%',
      features: [
        'Everything in Quarterly',
        'Best Value for Families!',
        'Premium Family Support',
        'Early Access Features'
      ],
      icon: Crown,
      popular: true
    }
  ];

  const currentPlans = planType === 'individual' ? individualPlans : householdPlans;

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscription/status`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          tier: tierId,
          origin_url: window.location.origin
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;
    
    setRedeemLoading(true);
    setRedeemError('');
    setRedeemSuccess('');

    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscription/redeem-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ code: inviteCode.toUpperCase().trim() })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to redeem code');
      }

      // Success! Show message
      setRedeemSuccess(data.message || '🎉 Lifetime access granted!');
      setInviteCode('');
      
      // Update local subscription state immediately
      setSubscriptionStatus({
        ...subscriptionStatus,
        status: 'active',
        has_access: true,
        lifetime_free: data.lifetime_free || true,
        tier: data.tier || 'premium'
      });
      
      // Redirect to dashboard after showing success message
      setTimeout(() => {
        // Force a full page reload to update all global state
        window.location.href = '/app/dashboard';
      }, 2000);
    } catch (error) {
      setRedeemError(error.message);
    } finally {
      setRedeemLoading(false);
    }
  };

  // If user already has active subscription
  if (!loading && subscriptionStatus?.has_access) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">You're All Set!</h1>
            <p className="text-gray-600 mb-4">
              {subscriptionStatus.lifetime_free 
                ? "You have lifetime free access through a family invite code!"
                : `You're subscribed to the ${subscriptionStatus.tier?.replace('_', ' ') || 'Premium'} plan.`}
            </p>
            {subscriptionStatus.expires_at && (
              <p className="text-sm text-gray-500">
                Renews: {new Date(subscriptionStatus.expires_at).toLocaleDateString()}
              </p>
            )}
            <button
              onClick={() => navigate('/app/dashboard')}
              className="mt-6 px-6 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>

        {/* Title Section */}
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Unlock <span className="text-emerald-600">Premium</span> Features
          </h1>
          <p className="text-gray-600 text-base max-w-xl mx-auto">
            Get the full GuapGoals experience with automatic bank sync, AI insights, and more
          </p>
        </div>

        {/* NEW: Guapo Vision Feature Highlight */}
        <div className="mb-8 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-1">
            <div className="bg-white rounded-xl p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center">
                  <span className="text-4xl">📸</span>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <h3 className="text-xl font-bold text-gray-900">Guapo Vision</h3>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">NEW</span>
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    <span className="font-semibold text-emerald-700">AI Statement Scanner</span> — Upload a photo or PDF of your bill. Guapo handles the data entry so your debt snowball is always 100% accurate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Type Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-200 p-1 rounded-xl inline-flex">
            <button
              onClick={() => setPlanType('individual')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                planType === 'individual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="individual-plans-tab"
            >
              Individual Plans
            </button>
            <button
              onClick={() => setPlanType('household')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                planType === 'household'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="household-plans-tab"
            >
              Household Plans
              <span className="ml-1 text-xs text-emerald-600">(Up to 6 users)</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        )}

        {/* Pricing Tiers */}
        {!loading && (
          <div className="grid md:grid-cols-3 gap-6">
            {currentPlans.map((plan) => {
              const Icon = plan.icon;
              const isPopular = plan.popular;
              
              return (
                <div
                  key={plan.id}
                  className={`relative bg-white border-2 rounded-2xl p-6 transition-all hover:shadow-lg ${
                    isPopular 
                      ? 'border-emerald-500 shadow-md' 
                      : 'border-gray-200'
                  }`}
                  data-testid={`tier-${plan.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                      BEST VALUE
                    </div>
                  )}
                  
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isPopular ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${isPopular ? 'text-emerald-600' : 'text-gray-600'}`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{plan.billing}</p>
                  
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  
                  {plan.savings && (
                    <div className="text-emerald-600 text-sm font-bold mb-4">{plan.savings}</div>
                  )}
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <Check className={`w-4 h-4 flex-shrink-0 ${isPopular ? 'text-emerald-500' : 'text-blue-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={checkoutLoading === plan.id}
                    className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                      isPopular
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {checkoutLoading === plan.id ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Get Started'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Invite Code Section - Collapsible */}
        <div className="mt-8 max-w-md mx-auto">
          <button
            onClick={() => setShowInviteCode(!showInviteCode)}
            className="flex items-center justify-center gap-2 text-purple-600 hover:text-purple-700 font-medium text-sm mx-auto transition-colors"
            data-testid="toggle-invite-code"
          >
            <Gift className="w-4 h-4" />
            Have an invite code?
            {showInviteCode ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {showInviteCode && (
            <div className="mt-4 bg-white border border-purple-200 rounded-xl p-4 shadow-sm">
              {redeemSuccess ? (
                /* Success State - Show big confirmation */
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-emerald-100 rounded-full flex items-center justify-center animate-bounce">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="text-emerald-700 font-bold text-lg">{redeemSuccess}</p>
                  <p className="text-gray-500 text-sm mt-2">Redirecting to dashboard...</p>
                </div>
              ) : (
                /* Input Form */
                <form onSubmit={handleRedeemCode} className="space-y-3">
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setRedeemError(''); // Clear error when typing
                    }}
                    placeholder="Enter 8-digit code"
                    maxLength={8}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder:text-gray-400 font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg"
                    data-testid="invite-code-input"
                  />
                  <button
                    type="submit"
                    disabled={redeemLoading || inviteCode.length !== 8}
                    className="w-full py-3 bg-purple-600 text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                    data-testid="redeem-code-btn"
                  >
                    {redeemLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Redeeming...
                      </>
                    ) : (
                      'Redeem Code'
                    )}
                  </button>
                  {redeemError && (
                    <div className="mt-2 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {redeemError}
                    </div>
                  )}
                </form>
              )}
            </div>
          )}
        </div>

        {/* Free Tier Info */}
        <div className="mt-8 bg-gray-100 rounded-xl p-6 max-w-2xl mx-auto">
          <h3 className="font-bold text-gray-900 mb-3">🆓 Free Tier Includes:</h3>
          <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gray-400" />
              Manual bill & income entry
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gray-400" />
              Basic budgeting tools
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gray-400" />
              Current month history only
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gray-400" />
              Basic Guapo AI assistance
            </div>
          </div>
        </div>

        {/* Payment Methods Info */}
        <div className="mt-8 text-center space-y-3">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-lg">
              <svg className="w-6 h-4" viewBox="0 0 24 16" fill="none">
                <rect width="24" height="16" rx="2" fill="#1A1F71"/>
                <path d="M9.5 10.5L10.5 5.5H12L11 10.5H9.5Z" fill="white"/>
                <path d="M15.5 5.5L13.5 10.5H12L14 5.5H15.5Z" fill="white"/>
              </svg>
              <span className="text-xs text-gray-600">Card</span>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-lg">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.66-2.2.47-3.06-.4C3.79 16.16 4.36 9.58 9.05 9.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 9.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="text-xs text-gray-600">Apple Pay</span>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 px-3 py-2 rounded-lg">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
              </svg>
              <span className="text-xs text-gray-600">Google Pay</span>
            </div>
          </div>
          <p className="text-gray-500 text-sm">
            🔒 Secure payment via Stripe • Cancel anytime • 7-day money-back guarantee
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
